import { normalizeWorkspaceMoney } from "./invoiceUtils";
import { normalizePrivateImportedPayments } from "./importedPaymentReview";
import { PUBLIC_GUEST_MODE } from "./publicGuestMode";

import { assertLocalPurchaseIntegrity } from './localPurchaseIntegrity';

export const normalizeLocalSnapshot = data => normalizePrivateImportedPayments(normalizeWorkspaceMoney(data));

const STANDARD_DATA_KEY = "invoice-studio-local-data-v1";
const STANDARD_BACKUP_KEY = "invoice-studio-local-data-v1-backup";
const GUEST_SAMPLE_DATA_KEY = "invoice-studio-public-guest-data-v1";
const GUEST_SAMPLE_BACKUP_KEY = "invoice-studio-public-guest-data-v1-backup";
const PORTAL_SAMPLE_DATA_KEY = "invoice-studio-business-portal-sample-v1";
const PORTAL_SAMPLE_BACKUP_KEY = "invoice-studio-business-portal-sample-v1-backup";
const GUEST_PERSONAL_DATA_KEY = "invoice-studio-public-local-data-v1";
const GUEST_PERSONAL_BACKUP_KEY = "invoice-studio-public-local-data-v1-backup";
const EMPTY_LOCAL_DATA_KEY = "invoice-studio-empty-local-data-v1";
const EMPTY_LOCAL_BACKUP_KEY = "invoice-studio-empty-local-data-v1-backup";
const GUEST_EMPTY_LOCAL_DATA_KEY = "invoice-studio-public-empty-local-data-v1";
const GUEST_EMPTY_LOCAL_BACKUP_KEY = "invoice-studio-public-empty-local-data-v1-backup";
const BACKUP_SCHEMA = "coetic-x.invoice-studio.backup";
const BACKUP_VERSION = 1;

const clone = (value) => JSON.parse(JSON.stringify(value));

const readEnvelope = (key) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return value?.version === BACKUP_VERSION && value?.data
      ? normalizePrivateImportedPayments(normalizeWorkspaceMoney(value.data))
      : null;
  } catch {
    return null;
  }
};

export const localStorageKeys = (space = "sample", guestMode = PUBLIC_GUEST_MODE) => {
  if (space === "portal-sample") return { dataKey: PORTAL_SAMPLE_DATA_KEY, backupKey: PORTAL_SAMPLE_BACKUP_KEY };
  if (space === "empty") return guestMode
    ? { dataKey: GUEST_EMPTY_LOCAL_DATA_KEY, backupKey: GUEST_EMPTY_LOCAL_BACKUP_KEY }
    : { dataKey: EMPTY_LOCAL_DATA_KEY, backupKey: EMPTY_LOCAL_BACKUP_KEY };
  if (!guestMode) return { dataKey: STANDARD_DATA_KEY, backupKey: STANDARD_BACKUP_KEY };
  if (space === "custom") return { dataKey: GUEST_PERSONAL_DATA_KEY, backupKey: GUEST_PERSONAL_BACKUP_KEY };
  return { dataKey: GUEST_SAMPLE_DATA_KEY, backupKey: GUEST_SAMPLE_BACKUP_KEY };
};

export const readLocalData = (space) => {
  if (typeof window === "undefined") return null;
  const { dataKey, backupKey } = localStorageKeys(space);
  return readEnvelope(dataKey) || readEnvelope(backupKey);
};

export const readLocalDataStrict = space => {
  const raw = window.localStorage.getItem(localStorageKeys(space).dataKey);
  const envelope = raw && JSON.parse(raw);
  if (envelope?.version !== BACKUP_VERSION || !envelope.data || typeof envelope.data !== 'object') throw new Error('The current local workspace cannot be read safely. Restore it before saving a purchase.');
  return { raw, data: normalizeLocalSnapshot(envelope.data) };
};

export const withLocalDataLock = async (space, action) => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) throw new Error('This browser cannot safely coordinate local saves. Use a current browser with Web Locks.');
  return navigator.locks.request('invoice-studio:snapshot:' + localStorageKeys(space).dataKey, { mode: 'exclusive', ifAvailable: true }, lock => {
    if (!lock) throw new Error('Another local save is in progress. Wait briefly, then retry.');
    return action();
  });
};

export const writeLocalDataLocked = async (data, space) => {
  try { return await withLocalDataLock(space, () => writeLocalData(data, space)); }
  catch (error) { return { ok: false, error }; }
};

// Synchronous primitive: live callers must hold withLocalDataLock.
export const writeLocalData = (data, space, options = {}) => {
  if (typeof window === "undefined") return { ok: false, error: new Error("Local storage is unavailable.") };
  const { dataKey, backupKey } = localStorageKeys(space);
  try {
    const current = window.localStorage.getItem(dataKey);
    if (Object.prototype.hasOwnProperty.call(options, 'expectedRaw') && current !== options.expectedRaw) throw new Error('Another view changed the local workspace. Reload before saving.');
    const previous = current ? JSON.parse(current).data : {};
    assertLocalPurchaseIntegrity(previous, data, options.appendPurchase === true);
    if (current) window.localStorage.setItem(backupKey, current);
    const committedData = normalizeLocalSnapshot(data);
    const raw = JSON.stringify({ version: BACKUP_VERSION, savedAt: new Date().toISOString(), data: committedData });
    window.localStorage.setItem(dataKey, raw);
    return { ok: true, raw, data: committedData };
  } catch (error) {
    return { ok: false, error };
  }
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

const portableImage = async (source) => {
  if (!source || source.startsWith("data:")) return source || "";
  try {
    const response = await fetch(source);
    if (!response.ok) return source;
    return await blobToDataUrl(await response.blob());
  } catch {
    return source;
  }
};

const withPortableImages = async (data) => {
  const portable = clone(data);
  portable.workspaces = await Promise.all((portable.workspaces || []).map(async (organization) => ({ ...organization, logo: await portableImage(organization.logo) })));
  portable.businesses = await Promise.all((portable.businesses || []).map(async (business) => ({ ...business, logo: await portableImage(business.logo) })));
  return portable;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const dateStamp = () => new Date().toISOString().slice(0, 10);

const createManifest = async (data) => ({
  schema: BACKUP_SCHEMA,
  version: BACKUP_VERSION,
  exportedAt: new Date().toISOString(),
  data: await withPortableImages(data),
});

const dataUrlParts = (value) => {
  const match = /^data:([^;,]+);base64,(.+)$/.exec(value || "");
  if (!match) return null;
  const extension = ({ "image/svg+xml": "svg", "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" })[match[1]] || "bin";
  return { extension, base64: match[2] };
};

export const exportBackup = async (data, format = "json") => {
  const manifest = await createManifest(data);
  const baseName = `invoice-studio-coetic-x-${dateStamp()}`;

  if (format === "json") {
    downloadBlob(new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" }), `${baseName}.json`);
    return `${baseName}.json`;
  }

  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  zip.file("invoice-studio-backup.json", JSON.stringify(manifest, null, 2));
  zip.file("README.txt", "Invoice Studio portable backup\n\nRestore this archive from Settings > Data and backups. The JSON manifest contains all records and embedded images.\n");

  const images = [
    ...(manifest.data.workspaces || []).map((item) => ({ id: `organization-${item.id}`, value: item.logo })),
    ...(manifest.data.businesses || []).map((item) => ({ id: `issuer-${item.id}`, value: item.logo })),
  ];
  images.forEach(({ id, value }) => {
    const asset = dataUrlParts(value);
    if (asset) zip.file(`images/${id}.${asset.extension}`, asset.base64, { base64: true });
  });

  downloadBlob(await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } }), `${baseName}.zip`);
  return `${baseName}.zip`;
};

const OPTIONAL_RECORD_LIMITS = {
  declarationDrafts: 10000,
  payrollProfiles: 10000,
  accountingSourceSummaries: 1000,
  accountingMappings: 10000,
  vatBookPeriods: 1000,
  fixedAssets: 100000,
  ledgerAccounts: 10000,
};

export const validateBackupManifest = (manifest) => {
  if (manifest?.schema !== BACKUP_SCHEMA || manifest?.version !== BACKUP_VERSION || !manifest?.data) throw new Error("This is not a supported Invoice Studio backup.");
  const data = manifest.data;
  for (const key of ["workspaces", "businesses", "clients", "services", "invoices", "activityLog"]) {
    if (!Array.isArray(data[key])) throw new Error(`Backup is missing ${key}.`);
  }
  Object.entries(OPTIONAL_RECORD_LIMITS).forEach(([key, limit]) => {
    if (data[key] === undefined) return;
    if (!Array.isArray(data[key])) throw new Error(`Backup field ${key} must be a list.`);
    if (data[key].length > limit) throw new Error(`Backup field ${key} exceeds the supported record limit.`);
  });
  if (data.declarationDrafts?.some((draft) => !draft?.scope?.organizationId || !draft?.scope?.businessId || !draft?.scope?.type || !draft?.scope?.period)) {
    throw new Error("Backup contains a declaration draft without a complete scope.");
  }
  if (data.payrollProfiles?.some((profile) => !profile?.id || !profile?.businessId)) {
    throw new Error("Backup contains a payroll profile without an id or issuing business.");
  }
  if (data.quickBooksImportEvidence !== undefined && data.quickBooksImportEvidence !== null && typeof data.quickBooksImportEvidence !== "object") {
    throw new Error("Backup field quickBooksImportEvidence must be an object.");
  }
  return normalizePrivateImportedPayments(normalizeWorkspaceMoney(data));
};

export const importBackup = async (file) => {
  if (!file) throw new Error("Choose a JSON or ZIP backup.");
  let manifest;
  if (file.name.toLowerCase().endsWith(".zip") || file.type === "application/zip") {
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(file);
    const manifestFile = zip.file("invoice-studio-backup.json") || Object.values(zip.files).find((entry) => !entry.dir && entry.name.endsWith(".json"));
    if (!manifestFile) throw new Error("The ZIP does not contain an Invoice Studio manifest.");
    manifest = JSON.parse(await manifestFile.async("string"));
  } else {
    manifest = JSON.parse(await file.text());
  }
  return validateBackupManifest(manifest);
};

export const localDataKey = localStorageKeys().dataKey;
export const localDataBackupKey = localStorageKeys().backupKey;
